/**
 * Authentication Store
 *
 * Lightweight auth store using localStorage + axios.
 * No Redux dependency — designed for React Context consumption.
 *
 * Generated by portal_generator.py
 */

import axios from 'axios';
import type {
  User,
  TokenPayload,
  LoginCredentials,
  LoginResponse,
  RefreshTokenResponse,
} from './types';

const AUTH_BASE_URL = '/api/v1/auth';

const TOKEN_KEYS = {
  access: 'access_token',
  refresh: 'refresh_token',
  expiry: 'token_expiry',
} as const;

// ---------------------------------------------------------------------------
// JWT Decoder
// ---------------------------------------------------------------------------

function decodeJWT(token: string): User | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed: TokenPayload = JSON.parse(decoded);

    return {
      id: parsed.sub || '',
      username: parsed.username || '',
      role: parsed.role || (parsed.roles ? parsed.roles.join(',') : ''),
      permissions: Array.isArray(parsed.permissions) ? parsed.permissions : [],
      tenant_id: parsed.tenant_id || parsed.tenant || null,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Token Storage
// ---------------------------------------------------------------------------

export const tokenStorage = {
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.access);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.refresh);
  },

  getExpiry(): number | null {
    const v = localStorage.getItem(TOKEN_KEYS.expiry);
    return v ? parseInt(v, 10) : null;
  },

  store(accessToken: string, refreshToken: string, expiresIn: number): void {
    localStorage.setItem(TOKEN_KEYS.access, accessToken);
    localStorage.setItem(TOKEN_KEYS.refresh, refreshToken);
    const expiry = Math.floor(Date.now() / 1000) + expiresIn;
    localStorage.setItem(TOKEN_KEYS.expiry, String(expiry));
  },

  clear(): void {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
    localStorage.removeItem(TOKEN_KEYS.expiry);
  },

  isExpired(): boolean {
    const expiry = tokenStorage.getExpiry();
    if (!expiry) return true;
    return Math.floor(Date.now() / 1000) >= expiry;
  },

  isExpiringSoon(bufferSeconds = 300): boolean {
    const expiry = tokenStorage.getExpiry();
    if (!expiry) return true;
    return (expiry - Math.floor(Date.now() / 1000)) < bufferSeconds;
  },
};

// ---------------------------------------------------------------------------
// Auth Actions
// ---------------------------------------------------------------------------

export async function loginAction(
  credentials: LoginCredentials,
): Promise<{ user: User; response: LoginResponse }> {
  // Omit tenant_id when not provided — the auth service applies its own
  // default (DEFAULT_TENANT_ID on single-tenant deployments, zero-tenant in
  // dev). Hardcoding the zero UUID here broke every re-tenanted platform.
  const payload: Record<string, unknown> = {
    username: credentials.username,
    password: credentials.password,
  };
  if (credentials.tenant_id) payload.tenant_id = credentials.tenant_id;
  const res = await axios.post<LoginResponse>(
    `${AUTH_BASE_URL}/login`,
    payload,
    { headers: { 'Content-Type': 'application/json' } },
  );

  const data = res.data;
  if (!data.access_token) {
    throw new Error('Invalid login response: missing access_token');
  }

  tokenStorage.store(data.access_token, data.refresh_token, data.expires_in);

  const user = decodeJWT(data.access_token);
  if (!user) {
    throw new Error('Failed to decode user from JWT');
  }

  // Persist the authenticated tenant — every generated client sends
  // X-Tenant-ID from localStorage (silent-session-expiry-fix / tenant gap).
  if (user.tenant_id) {
    localStorage.setItem('tenant_id', user.tenant_id);
  }

  return { user, response: data };
}

export async function logoutAction(): Promise<void> {
  const token = tokenStorage.getAccessToken();
  try {
    await axios.post(
      `${AUTH_BASE_URL}/logout`,
      {},
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
  } catch {
    // Ignore backend errors — still clear local state
  }
  tokenStorage.clear();
}

export async function refreshAction(): Promise<User> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const res = await axios.post<RefreshTokenResponse>(
    `${AUTH_BASE_URL}/refresh`,
    { refresh_token: refreshToken },
  );

  if (!res.data.access_token) {
    throw new Error('Invalid refresh response');
  }

  // The auth-service ROTATES refresh tokens (single-use) — store the rotated
  // one or the NEXT refresh is guaranteed to 401 (silent-session-expiry-fix).
  tokenStorage.store(res.data.access_token, res.data.refresh_token ?? refreshToken, res.data.expires_in);

  const user = decodeJWT(res.data.access_token);
  if (!user) {
    throw new Error('Failed to decode refreshed JWT');
  }

  return user;
}

/** Restore auth from localStorage (page reload) */
export function restoreAuth(): User | null {
  const token = tokenStorage.getAccessToken();
  if (!token || tokenStorage.isExpired()) {
    tokenStorage.clear();
    return null;
  }
  return decodeJWT(token);
}
